import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Search, Check, X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { ButtonSize, ButtonType, ButtonVariant } from '@/components/ui/Button/ButtonSize';
import { Card } from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import {
  useGetSectionByIdQuery,
  useGetSectionCategoriesQuery,
} from '@/components/features/sections/services/section.queries';
import {
  useAddSectionCategoryMutation,
  useDeleteSectionCategoryMutation,
} from '@/components/features/sections/services/section.mutations';
import { useGetCategoriesQuery } from '@/components/features/categories/services/categories.queries';
import useUserStore from '@/stores/useUserStore';

const SectionDetail = () => {
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const userStore = useUserStore(state => state.user);
  const isAdministrator = userStore.roles?.includes('administrator') || false;

  const { data: section } = useGetSectionByIdQuery(sectionId!);
  const { data: sectionCategories = [] } = useGetSectionCategoriesQuery(sectionId!);
  const { data: allCategories = [] } = useGetCategoriesQuery();
  const addCategoryMutation = useAddSectionCategoryMutation(sectionId!);
  const deleteCategoryMutation = useDeleteSectionCategoryMutation(sectionId!);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // Get categories that are not already in the section
  const availableCategories = useMemo(() => {
    const sectionCategoryIds = sectionCategories.map((sc) => sc.category.category_id);
    return allCategories.filter((cat) => !sectionCategoryIds.includes(cat.category_id));
  }, [allCategories, sectionCategories]);

  // Filter available categories based on search
  const filteredCategories = useMemo(() => {
    return availableCategories.filter((cat) =>
      cat.category_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableCategories, searchTerm]);

  const toggleCategorySelection = (categoryId: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleAddCategories = async () => {
    for (const categoryId of selectedCategoryIds) {
      await addCategoryMutation.mutateAsync({ category_id: categoryId });
    }
    setIsAddModalOpen(false);
    setSelectedCategoryIds([]);
    setSearchTerm('');
  };

  const handleDeleteCategory = async (sectionCategoryId: string) => {
    await deleteCategoryMutation.mutateAsync(sectionCategoryId);
  };

  return (
    <div className="h-full w-full">
      <div className="mx-auto bg-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant={ButtonVariant.GHOST}
            size={ButtonSize.SM}
            onClick={() => navigate('/sections')}
            type={ButtonType.BUTTON}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Πίσω
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{section?.section_name}</h1>
            <p className="mt-1 text-sm text-gray-500">Διαχείριση Κατηγοριών Ενότητας</p>
          </div>
        </div>

        {isAdministrator && (
          <div className="mb-6 flex justify-end">
            <Button
              onClick={() => setIsAddModalOpen(true)}
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              className="flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Προσθήκη Κατηγορίας
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sectionCategories.map((sectionCategory) => (
            <Card key={sectionCategory.section_category_id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">
                    {sectionCategory.category.category_name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {sectionCategory.category.description}
                  </p>
                </div>
                {isAdministrator && (
                  <Button
                    variant={ButtonVariant.GHOST}
                    size={ButtonSize.SM}
                    onClick={() => handleDeleteCategory(sectionCategory.section_category_id)}
                    type={ButtonType.BUTTON}
                    disabled={deleteCategoryMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>

        {sectionCategories.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500">Δεν υπάρχουν κατηγορίες σε αυτή την ενότητα</p>
          </div>
        )}
      </div>

      {/* Add Categories Modal */}
      <Modal
        open={isAddModalOpen}
        setOpen={setIsAddModalOpen}
        title="Προσθήκη Κατηγοριών"
        primaryAction={{
          text: addCategoryMutation.isPending
            ? 'Προσθήκη...'
            : `Προσθήκη (${selectedCategoryIds.length})`,
          onClick: handleAddCategories,
        }}
        secondaryAction={{
          text: 'Ακύρωση',
          onClick: () => {
            setIsAddModalOpen(false);
            setSelectedCategoryIds([]);
            setSearchTerm('');
          },
        }}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Αναζήτηση κατηγορίας..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {filteredCategories.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant={ButtonVariant.OUTLINE}
                  size={ButtonSize.SM}
                  onClick={() => {
                    const allIds = filteredCategories.map(c => c.category_id);
                    setSelectedCategoryIds(allIds);
                  }}
                  className="flex-1"
                  type={ButtonType.BUTTON}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Επιλογή Όλων ({filteredCategories.length})
                </Button>
                <Button
                  variant={ButtonVariant.OUTLINE}
                  size={ButtonSize.SM}
                  onClick={() => setSelectedCategoryIds([])}
                  className="flex-1"
                  type={ButtonType.BUTTON}
                  disabled={selectedCategoryIds.length === 0}
                >
                  <X className="w-4 h-4 mr-2" />
                  Αποεπιλογή Όλων
                </Button>
              </div>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredCategories.map((category) => (
              <div
                key={category.category_id}
                onClick={() => toggleCategorySelection(category.category_id)}
                className={`p-3 border rounded-md cursor-pointer transition-colors ${
                  selectedCategoryIds.includes(category.category_id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{category.category_name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                  </div>
                  {selectedCategoryIds.includes(category.category_id) && (
                    <Check className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2" />
                  )}
                </div>
              </div>
            ))}
            {filteredCategories.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                {availableCategories.length === 0
                  ? 'Όλες οι κατηγορίες έχουν προστεθεί'
                  : 'Δεν βρέθηκαν κατηγορίες'}
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SectionDetail;
